import { useRef, useState } from 'react'
import { Button, Checkbox, IconButton, Modal, Stack, TextField, Typography, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { etlAPI } from '../../../shared/api/ETLAPI'
import { FsAlertRef } from '../../common/atoms/Alert'
import { setLatestEtlStatus } from '../../../store/actions'

const FsRejectModal = ({
  isOpen,
  handleClose,
  setIsLoading,
}: {
  isOpen: boolean
  handleClose: () => void
  setIsLoading: (isLoading: boolean) => void
}) => {
  const { t } = useTranslation()
  const { etlFiles, latestEtlStatus } = useAppSelector((state) => state.currentEtl)
  const etlFileNames = etlFiles ? Object.keys(etlFiles) : []
  const dispatch = useAppDispatch()
  const alertRef = useRef<FsAlertRef>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [reason, setReason] = useState<string>('')
  const theme = useTheme()

  const handleCheckboxChange = (event: any, fileName: string) => {
    if (event.target.checked) setSelectedIds([...selectedIds, fileName])
    else setSelectedIds([...selectedIds.filter((id) => id !== fileName)])
  }

  const handleCloseModal = () => {
    setSelectedIds([])
    handleClose()
  }

  const handleReject = async () => {
    try {
      setIsLoading(true)
      if (latestEtlStatus) {
        const comment = `${
          selectedIds.length > 0 ? selectedIds.join(', ') : ''
        }||**||${reason ? `${reason}` : ''}`
        await etlAPI.setAcceptOrRejectFiles(latestEtlStatus.id, false, comment)
        await dispatch(setLatestEtlStatus())
        handleCloseModal()
      }
    } catch (error) {
      alertRef.current?.openAlert({
        severity: 'error',
        message: t('something.went.wrong'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        sx={{
          position: 'relative',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: 2,
          alignContent: 'flex-start',
          flexDirection: 'column',
          gap: 2,
          padding: 3,
          width: 600,
          height: 550,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{t('files.rejected')}</Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="body1">{t('select.rejected')}</Typography>
        <Typography variant="h6">{t('select.file')}</Typography>
        <Stack
          sx={{
            height: 200,
            overflowY: 'auto',
            border: 1,
            borderRadius: 1,
            borderColor: theme.palette.action.disabled,
          }}
        >
          {etlFileNames?.map((fileName) => (
            <Stack
              key={fileName}
              sx={{
                margin: 0.5,
                flexDirection: 'row',
                maxWidth: 300,
                alignItems: 'center',
                borderRadius: 2,
                backgroundColor: selectedIds.includes(fileName)
                  ? theme.palette.action.disabledBackground
                  : theme.palette.background.paper,
              }}
            >
              <Checkbox
                size="small"
                key={fileName}
                checked={selectedIds.includes(fileName)}
                onChange={(event) => handleCheckboxChange(event, fileName)}
                value={fileName}
              />
              {fileName}
            </Stack>
          ))}
        </Stack>
        <Typography variant="h6">{t('comment')}</Typography>
        <TextField
          multiline
          rows={3}
          label={t('type.here')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button variant="outlined" onClick={handleCloseModal}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleReject}
            disabled={selectedIds.length === 0 && !reason}
          >
            {t('send.client')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}

export default FsRejectModal
